import { startCase } from "lodash";
import { FC, useEffect, useState, useTransition } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  CheckboxTemplate,
  DateTemplate,
  DropdownBoundTemplate,
  DropdownTemplate,
  GroupTitleTemplate,
  MultiselectTemplate,
  TimeTemplate,
} from "@ryde/controls";
import {
  BOOKING_ADD_NEW_FORM_FIELDS,
  capitalize,
  DirectionEnum,
  DIRECTIONS_INCLUDE_BOTH,
  errors,
  IDropdownItem,
  IDropdownItemPassengers,
  Log,
  PassengerApi,
  PLURAL_KEY,
  requiredRuleStringField,
  systemSettingsDtoAtom,
  transformDropdownDtos,
  useFormats,
  warnAndToastException,
} from "@ryde/shared";

import { DrawerFormWrapperStyled } from "../../drawer";
import {
  BulkBookingHeaderPassengersStyled,
  BulkConflictsSection,
  BulkCreateBookingsFormBlockStyled,
  BulkCreateBookingsTimeBlockStyled,
  SourceDestinationDropdowns,
  UnaffiliatedUnitsAlert,
  useBulkBookingForm,
  useBulkBookingFormEffects,
  useUnaffiliatedUnitsWarning,
} from "../bulk-bookings-components";

export const AddNewBulkBookingsForm: FC = () => {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const { timeFormat } = useFormats();
  const systemSettingsDto = useRecoilValue(systemSettingsDtoAtom);
  const { disableShiftsDropdown, enableFlights, useFoods } =
    systemSettingsDto?.metadata ?? {};
  const [, startTransition] = useTransition();
  const watchedPassengersIds = watch(BOOKING_ADD_NEW_FORM_FIELDS.passengers);

  const direction = watch(BOOKING_ADD_NEW_FORM_FIELDS.direction);
  const [passengersDropdownItems, setPassengersDropdownItems] = useState<
    IDropdownItemPassengers[]
  >([]);
  const { onShiftSelect } = useBulkBookingForm();

  const onFlightSelect = useEvent((flight?: IDropdownItem) => {
    Log.debug("onFlightSelect event, flight:", flight);
  });

  useEffect(() => {
    const getItems = async (): Promise<void> => {
      try {
        const items = await PassengerApi.activePassengersDropdownItems();

        if (!items) {
          Log.warnAndToast(t("passenger.ACTIVE_PASSENGERS_NOT_FOUND"));
          return;
        }

        setPassengersDropdownItems(
          transformDropdownDtos({
            dtos: items,
            entity: "passenger",
          }),
        );
      } catch (error) {
        warnAndToastException({
          error,
          fallbackTranslationKey: errors.passengers.fetchActivePassengers,
          translationFunction: t,
          logWarnMessage:
            "Error while run activePassengersDropdownItems method",
        });
      }
    };

    startTransition(() => {
      getItems();
    });
  }, [setPassengersDropdownItems, t]);

  const { hasUnaffiliatedUnits } = useUnaffiliatedUnitsWarning({
    passengerItems: passengersDropdownItems,
    selectedPassengerIds: watchedPassengersIds ?? [],
    shiftFieldName: BOOKING_ADD_NEW_FORM_FIELDS.shiftId,
  });

  useBulkBookingFormEffects({
    direction,
  });

  return (
    <DrawerFormWrapperStyled>
      {hasUnaffiliatedUnits && <UnaffiliatedUnitsAlert />}
      <BulkBookingHeaderPassengersStyled>
        <BulkCreateBookingsFormBlockStyled>
          <MultiselectTemplate
            formLabel={t(`passenger.${PLURAL_KEY}`)}
            name={BOOKING_ADD_NEW_FORM_FIELDS.passengers}
            formAttributes={{
              selectItems: passengersDropdownItems,
              skipDropdownItemsTranslation: true,
            }}
            labelTranslated={capitalize(t(`passenger.${PLURAL_KEY}`))}
          />
          <DateTemplate
            name={BOOKING_ADD_NEW_FORM_FIELDS.date}
            labelTranslated={t("general.DATE")}
          />
        </BulkCreateBookingsFormBlockStyled>
      </BulkBookingHeaderPassengersStyled>
      {!disableShiftsDropdown && (
        <GroupTitleTemplate
          name="shifts"
          formLabel={startCase(t(`shift.${PLURAL_KEY}`))}
        />
      )}
      <BulkCreateBookingsFormBlockStyled
        $paddingTop={disableShiftsDropdown ? "24px" : "auto"}
      >
        {!disableShiftsDropdown && (
          <DropdownBoundTemplate
            name={BOOKING_ADD_NEW_FORM_FIELDS.shiftId}
            formLabel={startCase(t(`booking.formLabel.shiftId`))}
            formAttributes={{
              dropItemsSource: "shift",
              dropItemsDisableAdd: true,
            }}
            onSelect={onShiftSelect}
          />
        )}
        <DropdownTemplate
          name={BOOKING_ADD_NEW_FORM_FIELDS.direction}
          formLabel={startCase(t(`booking.formLabel.direction`))}
          formAttributes={{
            selectItems: DIRECTIONS_INCLUDE_BOTH,
            influencedBy: {
              valueKey: BOOKING_ADD_NEW_FORM_FIELDS.direction,
              controlName: BOOKING_ADD_NEW_FORM_FIELDS.shiftId,
            },
          }}
          formValidation={requiredRuleStringField}
        />
        <BulkCreateBookingsTimeBlockStyled>
          {(direction === DirectionEnum.PickUp ||
            direction === DirectionEnum.Both) && (
            <TimeTemplate
              name={BOOKING_ADD_NEW_FORM_FIELDS.arrivalTime}
              timeFormat={timeFormat}
              formLabel={t(`booking.bulk.arrivalTime`)}
              formValidation={requiredRuleStringField}
            />
          )}
          {(direction === DirectionEnum.DropOff ||
            direction === DirectionEnum.Both) && (
            <TimeTemplate
              name={BOOKING_ADD_NEW_FORM_FIELDS.departureTime}
              timeFormat={timeFormat}
              formLabel={t(`booking.bulk.departureTime`)}
              formValidation={requiredRuleStringField}
            />
          )}
        </BulkCreateBookingsTimeBlockStyled>
        <div className="checkbox_container">
          <CheckboxTemplate
            name={BOOKING_ADD_NEW_FORM_FIELDS.shouldReplaceExisting}
            formLabel={t(`booking.formLabel.shouldReplaceExisting`)}
          />
        </div>
      </BulkCreateBookingsFormBlockStyled>
      <GroupTitleTemplate
        name="details"
        formLabel={t(`booking.bulk.details`)}
      />
      <BulkCreateBookingsFormBlockStyled>
        <SourceDestinationDropdowns
          direction={direction}
          passengersIds={watchedPassengersIds}
        />
        {useFoods && (
          <>
            <GroupTitleTemplate
              name="mealTitle"
              formLabel={t(`booking.formLabel.mealTitle`)}
            />
            <DropdownBoundTemplate
              name={BOOKING_ADD_NEW_FORM_FIELDS.mealId}
              formLabel={t(`booking.formLabel.mealId`)}
              formAttributes={{
                dropItemsSource: "meal",
                dropItemsDisableAdd: true,
                // Filter meals by the selected shift only when the shift dropdown is rendered —
                // with disableShiftsDropdown, shiftId is never populated and the ancestor filter
                // would leave the meal dropdown permanently disabled.
                ...(!disableShiftsDropdown && {
                  dropdownItemsFilter: {
                    ancestorFieldName: BOOKING_ADD_NEW_FORM_FIELDS.shiftId,
                    ancestorItemsSource: "shift",
                    matchAncestorItemArrayKey: "mealIds",
                  },
                }),
              }}
            />
          </>
        )}
        <div className="checkbox_container">
          <CheckboxTemplate
            name={BOOKING_ADD_NEW_FORM_FIELDS.isDraft}
            formLabel={t(`booking.formLabel.isDraft`)}
          />
        </div>

        {enableFlights && (
          <DropdownBoundTemplate
            name={BOOKING_ADD_NEW_FORM_FIELDS.flightId}
            formLabel={startCase(t(`booking.formLabel.flightId`))}
            formAttributes={{
              dropItemsSource: "flight",
              dropItemsDisableAdd: true,
            }}
            onSelect={onFlightSelect}
          />
        )}
      </BulkCreateBookingsFormBlockStyled>
      <BulkConflictsSection />
    </DrawerFormWrapperStyled>
  );
};
